const { SUPPORTED_PLATFORMS, DEFAULT_SUPPORT_VERSIONS, IDENTITY } = require("../constants")
const { getPropertyPath } = require("./index")
const { getCookie } = require("./cookie")
const { gte } = require("./version")

/**
 * checks if the flow is SSR or not
 * @returns {boolean}
 */
const isSSR = () => typeof window === "undefined"

// supported native platform types (ios|android)
const platform = {
    ios: () => {
        if (isSSR()) return false
        const path =
            window.synapse &&
            window.synapse.config &&
            window.synapse.config.nativeBridge &&
            window.synapse.config.nativeBridge.iosBridgePath
        return getPropertyPath(window, path) || false
    },
    android: () => {
        if (isSSR()) return false
        const path =
            window.synapse &&
            window.synapse.config &&
            window.synapse.config.nativeBridge &&
            window.synapse.config.nativeBridge.androidBridgePath
        return getPropertyPath(window, path) || false
    },
}

/**
 * gets identity values from node.js request/response objects
 * @param {object} [req] - node.js based request object
 * @param {object} [res] - node.js based response object
 * @param {string} identity - name of identity key
 * @returns {boolean}
 */
const getIdentityValue = (req, res, identity = "") => {
    if (req || res) {
        const requestIdentity = (req && req[identity]) || (res && res[identity])
        const responseCookieIdentity =
            (req && req.cookies && req.cookies[identity]) || (res && res.cookies && res.cookies[identity])
        return requestIdentity || responseCookieIdentity
    }
    return false
}

/**
 * gets the platform value for the current environment (ios|android|web)
 * support for both CSR and SSR flows, in non-webview env defaults to web
 * @param {object} [req] - node.js based request object
 * @param {object} [res] - node.js based response object
 * @returns {string}
 */
const getPlatform = (req, res) => {
    if (req || res) {
        const currentPlatform = getIdentityValue(req, res, IDENTITY.PLATFORM)
        if (currentPlatform) return currentPlatform
    }
    const currentPlatform = getCookie(IDENTITY.PLATFORM)
    if (currentPlatform === SUPPORTED_PLATFORMS.IOS && platform.ios()) return SUPPORTED_PLATFORMS.IOS
    else if (currentPlatform === SUPPORTED_PLATFORMS.ANDROID && platform.android())
        return SUPPORTED_PLATFORMS.ANDROID
    return SUPPORTED_PLATFORMS.WEB
}

/**
 * performs webview identification, support both SSR and CSR flows
 * @param {object} [req] - node.js based request object
 * @param {object} [res] - node.js based response object
 * @returns {boolean}
 */
const isWebView = (req, res) => {
    if (req || res) {
        const webviewInit = getIdentityValue(req, res, IDENTITY.INIT)
        return !!webviewInit && webviewInit !== "false"
    }
    return !!(!isSSR() && getPlatform() !== SUPPORTED_PLATFORMS.WEB && getCookie(IDENTITY.INIT))
}

/**
 * determines if the current webview flow is legacy based or not
 * @param {object} [req] - node.js based request object
 * @param {object} [res] - node.js based response object
 * @returns {boolean}
 */
const isLegacyWebView = (req, res) => {
    if (req || res) {
        const legacyWebView = getIdentityValue(req, res, IDENTITY.LEGACY)
        return !!legacyWebView && legacyWebView !== "false"
    }
    return false
}

/**
 * sanitizes the json string while bridge callback communication
 * @param {string} data
 * @returns {string}
 */
const jsonDataSantizer = (data = "") => {
    const platform = getPlatform()
    if (platform === SUPPORTED_PLATFORMS.ANDROID) return data.replace(/\\/g, "")
    return data
}

/**
 * gets the platform prefix for the native bridge
 * @returns {object|boolean}
 */
const getPlatformPrefix = () => {
    const currentPlatform = getCookie(IDENTITY.PLATFORM)
    if (currentPlatform === SUPPORTED_PLATFORMS.IOS && platform.ios()) return platform.ios()
    else if (currentPlatform === SUPPORTED_PLATFORMS.ANDROID && platform.android()) return platform.android()
    else return {}
}

/**
 * @typedef supportObject
 * @type {object}
 * @property {string} android - support version of android platform (major.minor.patch)
 * @property {string} ios - support version of ios platform (major.minor.patch)
 */

/**
 * checks support for feature based on current and support version passed
 * @param {supportObject} [support] - support version object
 * @returns {boolean}
 */
const checkVersionSupport = (support = DEFAULT_SUPPORT_VERSIONS) => {
    const identityConfig = getCookie(IDENTITY.CONFIG)
    if (identityConfig && identityConfig.version) {
        const currentVersion = identityConfig.version
        const supportVersion = support[identityConfig.platform]
        if (currentVersion && supportVersion) {
            return gte(currentVersion, supportVersion)
        }
    }
    return false
}

module.exports = {
    isSSR,
    isWebView,
    platform,
    getPlatform,
    isLegacyWebView,
    jsonDataSantizer,
    getPlatformPrefix,
    checkVersionSupport,
}
